import React, { useEffect, useState } from "react";
import styles from "./Generate.module.css";
import { CustomAccordion, CustomButton } from "../../../../../common/components";
import { translate } from "../../../../../common/providers";
import { useApi } from "../../../../../common/hooks";
import { ContentService } from "../../../../../api";
import { useDispatch, useSelector } from "react-redux";
import { UserPrompts, GenerationActions, MasterDialog } from "./components";
import { SavedContents } from "./components";
import {
  setGenOpenSavedDeliv,
  setGenOpenSavedProject,
  setGenOpenSavedQA,
} from "../../../slice/multi-project-search-slice";

const Generate = () => {
  const { call: getSavedContent } = useApi(ContentService.getSavedContent);
  const { call: deleteAllSavedContent } = useApi(ContentService.deleteAllSavedContent);
  const { call: deleteSavedContent } = useApi(ContentService.deleteSavedContent);
  const { call: getGenerationState } = useApi(ContentService.getGenerationState);
  const { call: saveGenerationState } = useApi(ContentService.saveGenerationState);
  const userEmail = useSelector(({ context }) => context.user.email);
  const projectId = useSelector(({ context }) => context.project?.id);
  const genOpenSavedQA = useSelector(({ multiProjectSearch }) => multiProjectSearch.genOpenSavedQA);
  const genOpenSavedDeliv = useSelector(({ multiProjectSearch }) => multiProjectSearch.genOpenSavedDeliv);
  const genOpenSavedProject = useSelector(({ multiProjectSearch }) => multiProjectSearch.genOpenSavedProject);
  const [savedProjectContent, setSavedProjectContent] = useState([]);
  const [savedDeliverableContent, setSavedDeliverableContent] = useState([]);
  const [additionalContext, setAdditionalContext] = useState([]);
  const [stateHasChanges, setStateHasChanges] = useState(false);
  const [openMasterDialog, setOpenMasterDialog] = useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    getSavedContent({ projectId: undefined })
      .then((data) => setSavedDeliverableContent(data))
      .catch(console.error);
    if (projectId) {
      getSavedContent({ projectId })
        .then((data) => setSavedProjectContent(data))
        .catch(console.error);
    }
  }, [projectId]);
  useEffect(() => {
    getGenerationState()
      .then((data) => {
        if (data) {
          setAdditionalContext(data.userPrompts);
        }
      })
      .catch(console.error);
  }, []);
  useEffect(() => {
    if (stateHasChanges) {
      saveGenerationState({ userPrompts: additionalContext })
        .then(() => setStateHasChanges(false))
        .catch(console.error);
    }
  }, [additionalContext, stateHasChanges]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (
        e.ctrlKey &&
        (userEmail.includes("@aitenders.com") || userEmail.includes("@sc3.com")) &&
        (e.key === "0" || e.code === "Digit0" || e.code === "Numpad0")
      ) {
        setOpenMasterDialog(true);
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  const handleDeleteAllDeliverable = () => {
    deleteAllSavedContent({ projectId: undefined })
      .then(() => setSavedDeliverableContent([]))
      .catch(console.error);
  };
  const handleDeleteAllProject = () => {
    deleteAllSavedContent({ projectId })
      .then(() => setSavedProjectContent([]))
      .catch(console.error);
  };
  const handleDeleteAllUserContext = () => {
    setAdditionalContext([]);
    setStateHasChanges(true);
  };
  const handleRemoveUserPrompt = (value) => {
    setAdditionalContext((prev) => prev.filter((p) => p !== value));
    setStateHasChanges(true);
  };

  const handleAddUserPrompt = (newUserPromp) => {
    setAdditionalContext((prev) => [...prev, newUserPromp]);
    setStateHasChanges(true);
  };
  const handleDeleteProjectContent = (idToDelete) => {
    deleteSavedContent({ savedDataId: idToDelete })
      .then(() => setSavedProjectContent((prev) => prev.filter((p) => p.savedDataId !== idToDelete)))
      .catch(console.error);
  };
  const handleDeleteDeliverableContent = (idToDelete) => {
    deleteSavedContent({ savedDataId: idToDelete })
      .then(() => setSavedDeliverableContent((prev) => prev.filter((p) => p.savedDataId !== idToDelete)))
      .catch(console.error);
  };
  const handleExpandSavedQA = () => {
    dispatch(setGenOpenSavedQA(!genOpenSavedQA));
  };
  const handleExpandSavedDeliv = () => {
    dispatch(setGenOpenSavedDeliv(!genOpenSavedDeliv));
  };
  const handleExpandSavedProject = () => {
    dispatch(setGenOpenSavedProject(!genOpenSavedProject));
  };
  const handleClose = () => {
    setOpenMasterDialog(false);
  };
  return (
    <div className={styles.container}>
      <div className={styles.innerContainer}>
        <CustomAccordion
          className={styles.accordionContent}
          defaultOpen={genOpenSavedQA}
          header={
            <div className={styles.boxTitle}>
              <span>{translate("multi-projects-search.generate.additional-instructions")}</span>
              <CustomButton
                color="secondary"
                disabled={additionalContext.length <= 0}
                variant="outlined"
                onClick={handleDeleteAllUserContext}
              >
                {translate("common:btn.delete-all")}
              </CustomButton>
            </div>
          }
          onExpand={handleExpandSavedQA}
        >
          <UserPrompts
            additionalContext={additionalContext}
            onAdd={handleAddUserPrompt}
            onRemove={handleRemoveUserPrompt}
          />
        </CustomAccordion>
        <div className={`${styles.boxTitle} ${styles["boxTitle--sources"]}`}>
          {translate("multi-projects-search.summary-side-panel.tabs.sources")}
        </div>
        <div className={styles.sourceContainer}>
          <CustomAccordion
            className={styles.accordionContent}
            defaultOpen={genOpenSavedDeliv}
            header={
              <div className={`${styles.boxTitle} ${styles["text--regular"]}`}>
                <span>
                  {translate("multi-projects-search.tab-header.deliverable")}&nbsp;({savedDeliverableContent.length})
                </span>
                <CustomButton
                  color="secondary"
                  disabled={savedDeliverableContent <= 0}
                  variant="outlined"
                  onClick={handleDeleteAllDeliverable}
                >
                  {translate("common:btn.delete-all")}
                </CustomButton>
              </div>
            }
            onExpand={handleExpandSavedDeliv}
          >
            <SavedContents savedContents={savedDeliverableContent} onDelete={handleDeleteDeliverableContent} />
          </CustomAccordion>
          <CustomAccordion
            className={styles.accordionContent}
            defaultOpen={genOpenSavedProject}
            header={
              <div className={`${styles.boxTitle} ${styles["text--regular"]}`}>
                <span>
                  {translate("multi-projects-search.tab-header.project")}&nbsp;({savedProjectContent.length})
                </span>
                <CustomButton
                  color="secondary"
                  disabled={!projectId || savedProjectContent.length <= 0}
                  variant="outlined"
                  onClick={handleDeleteAllProject}
                >
                  {translate("common:btn.delete-all")}
                </CustomButton>
              </div>
            }
            onExpand={handleExpandSavedProject}
          >
            <SavedContents
              projectContentDeletion
              savedContents={savedProjectContent}
              onDelete={handleDeleteProjectContent}
            />
          </CustomAccordion>
        </div>
      </div>
      <GenerationActions
        savedDeliverableContentCount={savedDeliverableContent.length}
        savedProjectContentCount={savedProjectContent.length}
        userPromptCount={additionalContext.length}
      />
      <MasterDialog open={openMasterDialog} onClose={handleClose} />
    </div>
  );
};

export default Generate;
